import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaTrash, FaDownload, FaSearch } from 'react-icons/fa';
import {FaImage, FaTable,
  FaFileAlt,FaVideo,FaCube,FaFile,FaFileCode,
  FaFileArchive,FaFileAudio,FaFilePdf,
  FaFilePowerpoint,FaFileWord,FaFileExcel,FaFileDownload,FaDatabase,FaCode, FaFont,FaKey
} from 'react-icons/fa';
import { uploadFile, getFile, deleteFile } from '../../api/files';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function FilestorageComponent({ component, onUpdate, refreshSidebar }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { files: [] };
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name-asc');
  const [componentWidth, setComponentWidth] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Track component width
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setComponentWidth(entries[0].contentRect.width);
      }
    });
    if (dropZoneRef.current) {
      resizeObserver.observe(dropZoneRef.current);
    }
    return () => {
      if (dropZoneRef.current) {
        resizeObserver.unobserve(dropZoneRef.current);
      }
    };
  }, []);

  // Log component and validate files
  useEffect(() => {
    console.log('Component:', component);
    console.log('Component width:', componentWidth);
    const invalidFiles = data.files.filter(f => !f.path);
    if (invalidFiles.length > 0) {
      console.warn('Files missing path:', invalidFiles);
    }
  }, [component, componentWidth]);

  // Fetch image previews
  useEffect(() => {
    const fetchImages = async () => {
      const urls = {};
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.psd'];
      for (const file of data.files.filter(f => f.path && imageExtensions.includes(getExtension(f.name)))) {
        try {
          const blob = await getFile(file.path, localStorage.getItem('access_token'));
          urls[file.path] = URL.createObjectURL(blob);
        } catch (error) {
          console.error(`Failed to load image ${file.path}:`, error);
        }
      }
      setImageUrls(urls);
    };
    fetchImages();

    return () => {
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [data.files]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) {
      alert('Please drop a valid file.');
      return;
    }

    await handleUploadFile(file);
  };

  const handleUploadFile = async (fileOrEvent) => {
    const file = fileOrEvent instanceof File ? fileOrEvent : fileOrEvent.target.files[0];
    if (!file) {
      alert('Please select a valid file.');
      return;
    }

    setIsUploading(true);
    try {
      const response = await uploadFile(component.idea_id, file, localStorage.getItem('access_token'));
      console.log('Upload response:', response);
      const path = response.path || response.file_path || response.filePath;
      if (!path) {
        throw new Error('No path returned in upload response');
      }
      const newFiles = [
        ...data.files,
        {
          path,
          name: file.name,
          size: file.size,
          uploaded_at: new Date().toISOString()
        }
      ];
      onUpdate({
        ...component,
        data: { files: newFiles }
      });
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.kind === 'file') {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Add a brief visual feedback for paste
          const originalBg = dropZoneRef.current?.style.backgroundColor;
          if (dropZoneRef.current) {
            dropZoneRef.current.style.backgroundColor = 'var(--bg-hover)';
            dropZoneRef.current.style.transition = 'background-color 0.3s';
          }
          
          await handleUploadFile(file);
          
          // Reset background after a delay
          setTimeout(() => {
            if (dropZoneRef.current) {
              dropZoneRef.current.style.backgroundColor = originalBg || '';
            }
          }, 500);
        }
        return;
      }
    }
  };

  const handleDelete = async (file) => {
    if (!file.path) {
      alert('Cannot delete file: missing path');
      return;
    }
    setDeleteConfirm(file);
  };

  const confirmDelete = async () => {
    const file = deleteConfirm;
    setDeleteConfirm(null);

    try {
      await deleteFile(file.path, localStorage.getItem('access_token'));
      if (imageUrls[file.path]) {
        URL.revokeObjectURL(imageUrls[file.path]);
        setImageUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[file.path];
          return newUrls;
        });
      }
      const newFiles = data.files.filter(f => f !== file);
      onUpdate({
        ...component,
        data: { files: newFiles }
      });
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
    } catch (error) {
      alert(`Deletion failed: ${error.message}`);
    }
  };

  const handleDownload = async (file) => {
    if (!file.path) {
      alert('Cannot download file: missing path');
      return;
    }
    try {
      const blob = await getFile(file.path, localStorage.getItem('access_token'));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Download failed: ${error.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    console.log('Parsing date:', dateStr);
    const date = new Date(dateStr);
    return date instanceof Date && !isNaN(date) ? date.toLocaleString() : 'Unknown';
  };

  const getExtension = (name) => {
    return name.includes('.') ? `.${name.split('.').pop().toLowerCase()}` : '';
  };

const getFileTypeIcon = (name) => {
  const ext = getExtension(name).toLowerCase();
  
  // Image formats
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.svg', '.webp', '.psd', '.ai', '.eps', '.ico'];
  
  // Spreadsheet formats
  const excelExts = ['.xls', '.xlsx', '.csv', '.ods', '.tsv'];
  
  // Document formats
  const docExts = ['.doc', '.docx', '.txt', '.rtf', '.odt', '.tex', '.wpd'];
  
  // Video formats
  const videoExts = ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.mpeg', '.mpg'];
  
  // 3D model formats
  const threeDExts = ['.stl', '.obj', '.fbx', '.3ds', '.dae', '.blend', '.skp', '.step', '.iges'];
  
  // Audio formats
  const audioExts = ['.mp3', '.wav', '.aac', '.flac', '.ogg', '.m4a', '.wma'];
  
  // Archive/compressed formats
  const archiveExts = ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.dmg', '.iso'];
  
  // Code formats
  const codeExts = ['.js', '.jsx', '.ts', '.tsx', '.html', '.css', '.scss', '.py', '.java', '.cpp', '.c', '.h', '.php', '.sh', '.json', '.xml', '.yml', '.yaml', '.sql'];
  
  // Executable/System formats
  const exeExts = ['.exe', '.msi', '.bat', '.cmd', '.dll', '.sys'];
  
  // CAD/Drawing formats
  const cadExts = ['.dwg', '.dxf', '.dgn', '.ipt', '.iam', '.catpart', '.catproduct'];
  
  // Font formats
  const fontExts = ['.ttf', '.otf', '.woff', '.woff2', '.eot'];
  
  // Database formats
  const dbExts = ['.db', '.sqlite', '.mdb', '.accdb', '.sql'];
  
  // Security/certificate formats
  const securityExts = ['.pem', '.cer', '.crt', '.key', '.pfx', '.p12'];

  if (imageExts.includes(ext)) return <FaImage />;
  if (excelExts.includes(ext)) return <FaFileExcel />;
  if (docExts.includes(ext)) return <FaFileWord />;
  if (videoExts.includes(ext)) return <FaVideo />;
  if (threeDExts.includes(ext)) return <FaCube />;
  if (audioExts.includes(ext)) return <FaFileAudio />;
  if (archiveExts.includes(ext)) return <FaFileArchive />;
  if (codeExts.includes(ext)) return <FaCode />;
  if (exeExts.includes(ext)) return <FaFileDownload />;
  if (cadExts.includes(ext)) return <FaFileCode />; // Alternative: could use a CAD-specific icon if available
  if (fontExts.includes(ext)) return <FaFont />;
  if (dbExts.includes(ext)) return <FaDatabase />;
  if (securityExts.includes(ext)) return <FaKey />;
  if (ext === '.pdf') return <FaFilePdf />;
  if (ext === '.ppt' || ext === '.pptx') return <FaFilePowerpoint />;
  
  return <FaFile />;
};

  const filteredFiles = data.files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    const [key, order] = sortOption.split('-');
    let valA, valB;
    if (key === 'extension') {
      valA = getExtension(a.name);
      valB = getExtension(b.name);
    } else if (key === 'uploaded_at') {
      valA = new Date(a.uploaded_at).getTime();
      valB = new Date(b.uploaded_at).getTime();
    } else if (key === 'name') {
      valA = a.name.toLowerCase();
      valB = b.name.toLowerCase();
    } else {
      valA = a[key];
      valB = b[key];
    }
    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div
      ref={dropZoneRef}
      className={`w-full h-full flex flex-col p-0 outline-none focus:outline-none focus:shadow-[0_0_0_2px_var(--secondary)] focus:shadow-opacity-30 rounded-lg transition-all duration-200 ${isDragging ? 'border-2 border-dashed border-[var(--primary)] bg-[var(--bg-hover)]/50' : ''}`}
      tabIndex={0}
      onPaste={handlePaste}
      onFocus={() => {}} // Make it focusable for paste events
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      title="Click here and press Ctrl+V to paste files"
    >
      {/* Header with Add File, Search, and Sort */}
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex justify-between items-center gap-2">
          <label className="cursor-pointer p-2 bg-[var(--primary)] text-white rounded hover:opacity-90 flex items-center gap-2 disabled:opacity-50">
            <FaPlus /> {isUploading ? 'Uploading...' : 'Add File'}
            <input
              type="file"
              className="hidden"
              ref={fileInputRef}
              onChange={handleUploadFile}
              disabled={isUploading}
            />
          </label>
          <select
            className="p-2 border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            aria-label="Sort files"
          >
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="extension-asc">Extension (A-Z)</option>
            <option value="extension-desc">Extension (Z-A)</option>
            <option value="size-asc">Size (Smallest)</option>
            <option value="size-desc">Size (Largest)</option>
            <option value="uploaded_at-asc">Upload Date (Oldest)</option>
            <option value="uploaded_at-desc">Upload Date (Newest)</option>
          </select>
        </div>
        <div className="relative w-full">
          <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[var(--text-secondary)]" />
          <input
            type="text"
            className="w-full pl-8 p-2 border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search files"
          />
        </div>
        
        {!isUploading && (
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            You can also paste files directly with Ctrl+V or drag them anywhere
          </p>
        )}
        
        {isUploading && (
          <div className="text-center text-[var(--text-secondary)] text-sm mt-2">
            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary)] mr-2"></div>
            Uploading file...
          </div>
        )}
      </div>

      {/* File List */}
      <div className={`flex-1 min-h-0 overflow-y-auto border border-[var(--border-color)] rounded`}>
        {sortedFiles.length === 0 ? (
          <div className="text-[var(--text-secondary)] text-center p-4">
            <p className="mb-2">No files found.</p>
            <p className="text-sm">
              Add files by clicking "Add File", dragging them here, or{' '}
              <span className="font-medium text-[var(--secondary)]">paste files (Ctrl+V)</span>.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {sortedFiles.map((file, index) => (
              <li
                key={index}
                className="p-3 bg-[var(--bg-primary)] border-b border-[var(--border-color)] flex items-center gap-3 group hover:bg-[var(--bg-hover)]"
              >
                {/* Preview/Icon - hidden on very small component width */}
                {componentWidth >= 300 && (
                  <div className="flex-shrink-0">
                    {['.jpg', '.jpeg', '.png', '.gif'].includes(getExtension(file.name)) && imageUrls[file.path] ? (
                      <img
                        src={imageUrls[file.path]}
                        alt={file.name}
                        className="w-10 h-10 object-contain rounded"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-[var(--bg-secondary)] rounded flex items-center justify-center text-[var(--text-secondary)] text-xl">
                        {getFileTypeIcon(file.name)}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex-1 min-w-0 flex flex-col">
                  <p
                    className={`text-[var(--text-primary)] ${componentWidth < 400 ? 'max-w-[100px]' : 'max-w-[200px]'} truncate`}
                    title={file.name}
                  >
                    {file.name}
                  </p>
                  {/* File details - below on small component width */}
                  {componentWidth < 400 && (
                    <p className="text-xs text-[var(--text-secondary)]">
                      {formatFileSize(file.size)} • {formatDate(file.uploaded_at)}
                    </p>
                  )}
                </div>
                
                {/* File details - inline on larger component width */}
                {componentWidth >= 400 && (
                  <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)] whitespace-nowrap">
                    <span>{formatFileSize(file.size)}</span>
                    <span>•</span>
                    <span>{formatDate(file.uploaded_at)}</span>
                  </div>
                )}
                
                <div className="flex gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-0.5 text-[var(--text-secondary)] hover:text-[var(--primary)]"
                    title="Download"
                  >
                    <FaDownload />
                  </button>
                  <button
                    onClick={() => handleDelete(file)}
                    className="p-0.5 text-[var(--text-secondary)] hover:text-danger"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] animate-fade-in">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl border border-[var(--border-color)] animate-scale-in">
            <h3 className="text-lg font-medium mb-4 text-[var(--text-primary)]">Delete File</h3>
            <p className="mb-6 text-[var(--text-secondary)]">
              Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg bg-[var(--bg-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg bg-danger hover:bg-danger hover:shadow-lg hover:brightness-110 text-white transition-all duration-200 transform hover:scale-105"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}